import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Loader2 } from "lucide-react";

/**
 * One-off manual lead entry. Everything's optional except business name +
 * the 3 backend-required identity fields (industry / city / state).
 * Same POST /api/leads/import endpoint the CSV importer uses.
 */
export default function ManualAddLeadModal({ onClose, onAdded }) {
  const [f, setF] = useState({
    name: "",
    industry: "",
    city: "",
    state: "",
    email: "",
    phone: "",
    contact_name: "",
    notes: "",
    source: "Manual",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Business name is required.");
    if (!f.industry.trim() || !f.city.trim() || !f.state.trim()) {
      return toast.error("Industry, city, and state are required.");
    }
    setBusy(true);
    try {
      const notesParts = [
        f.contact_name ? `Contact: ${f.contact_name}` : "",
        `Source: ${f.source || "Manual"}`,
        f.notes || "",
      ].filter(Boolean);
      await api.post("/leads/import", {
        name: f.name.trim(),
        industry: f.industry.trim(),
        city: f.city.trim(),
        state: f.state.trim().toUpperCase(),
        email: f.email.trim(),
        phone: f.phone.trim(),
        notes: notesParts.join(" · "),
        has_website: false,
      });
      toast.success(`Added "${f.name}" to the pipeline.`);
      onAdded?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="manual-lead-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white w-full max-w-lg border border-neutral-900 shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-amber-300">Quick add</div>
            <div className="text-xl font-bold mt-1">New lead — solo entry</div>
            <div className="text-xs text-neutral-400 mt-0.5">For one-offs. Use "Import CSV" for bulk.</div>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="manual-lead-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          <Row label="Business name *" testId="manual-lead-name">
            <input required autoFocus value={f.name} onChange={upd("name")} placeholder="Sunrise HVAC" className="lf-input" />
          </Row>
          <div className="grid grid-cols-3 gap-3">
            <Row label="Industry *" testId="manual-lead-industry">
              <input required value={f.industry} onChange={upd("industry")} placeholder="HVAC" className="lf-input" />
            </Row>
            <Row label="City *" testId="manual-lead-city">
              <input required value={f.city} onChange={upd("city")} placeholder="Austin" className="lf-input" />
            </Row>
            <Row label="State *" testId="manual-lead-state">
              <input required value={f.state} onChange={upd("state")} placeholder="TX" maxLength={2} className="lf-input uppercase" />
            </Row>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Email" testId="manual-lead-email">
              <input type="email" value={f.email} onChange={upd("email")} placeholder="owner@sunrisehvac.com" className="lf-input" />
            </Row>
            <Row label="Phone" testId="manual-lead-phone">
              <input value={f.phone} onChange={upd("phone")} placeholder="+15125550123" className="lf-input font-mono" />
            </Row>
          </div>
          <Row label="Contact person" testId="manual-lead-contact">
            <input value={f.contact_name} onChange={upd("contact_name")} placeholder="Jane Smith (owner)" className="lf-input" />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Source tag" testId="manual-lead-source">
              <input value={f.source} onChange={upd("source")} placeholder="Manual, Referral, Cold call…" className="lf-input" />
            </Row>
          </div>
          <Row label="Notes" testId="manual-lead-notes">
            <textarea rows={3} value={f.notes} onChange={upd("notes")} placeholder="Anything worth remembering — past interactions, quote history, gatekeeper name…" className="lf-input resize-y" />
          </Row>
        </div>

        <div className="border-t border-neutral-100 px-6 py-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="lf-btn-outline text-sm">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            className="lf-btn-primary text-sm inline-flex items-center gap-2"
            data-testid="manual-lead-submit"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : <><Plus className="w-4 h-4" /> Add lead</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function Row({ label, testId, children }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-xs text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
